/**
 * Quem abre o Financeiro, e em quais unidades.
 *
 * 🚨 A REGRA (decisão do PM, 20/10/2026): "O Financeiro deve aparecer para
 * qualquer admin de unidade, desde que o recurso esteja ativado nas unidades a
 * que ele está vinculado." Vale também para o gerente de rede.
 *
 * - `super_admin`: sempre, sem recorte. Para ele nada muda: em "Todas as
 *   unidades" quem diz o que ele vê continua sendo o banco.
 * - `tenant_admin` e `network_manager`: só nas unidades dele com a chave do
 *   Financeiro ligada (`credit_v2_fin_settings.fin_enabled`, que o super_admin
 *   vira em Gerenciamento → Unidades). Sem nenhuma, o módulo não existe para ele.
 * - vendedor, backoffice e agência: nunca.
 *
 * O papel é da pessoa, não da unidade (`credit_v2_user_roles`): quem é
 * administrador numa unidade é administrador em todas as dela. Por isso a régua
 * é "papel da pessoa" × "chave de cada unidade dela".
 *
 * "Unidade dela" é a lista do seletor do topo (o `tenants` do `TenantContext`,
 * sem unidade desativada). A chave lida do banco já vem só das unidades em que o
 * vínculo está ativo, porque a leitura passa pela política da tabela: vínculo
 * suspenso não liga nada.
 *
 * ⚠️ ESTA É A RÉGUA DA TELA, NÃO A FRONTEIRA. O menu, o portão da rota e o
 * recorte do módulo perguntam aqui para não oferecer o que o banco recusaria.
 * Quem decide de verdade é a política das tabelas `credit_v2_fin_*`.
 *
 * Substitui a lista fixa do piloto (`pilot-allowlist.ts`, só o Michel), que saiu
 * do código junto com esta regra.
 *
 * Pura: sem React, sem banco. Testada em `tests/lib/financeiro/acesso-ao-financeiro.test.ts`.
 */

/** Os papéis que abrem o Financeiro pela chave de cada unidade. */
export const PAPEIS_PELA_CHAVE_DA_UNIDADE = ["tenant_admin", "network_manager"] as const

/**
 * Por que a pessoa não abre o Financeiro. Viaja como código: quem escreve a
 * frase que a pessoa lê é a tela.
 *
 * - `papel`: o papel dela não abre o Financeiro em unidade nenhuma.
 * - `sem_unidade_ligada`: o papel abriria, mas nenhuma unidade dela tem o
 *   Financeiro ligado.
 */
export type MotivoSemFinanceiro = "papel" | "sem_unidade_ligada"

/** O que a regra precisa de cada unidade da pessoa. */
export interface UnidadeDaPessoa {
  id: string
  deactivated_at?: string | null
}

export interface AcessoAoFinanceiro {
  pode: boolean
  motivo: MotivoSemFinanceiro | null
  /**
   * As unidades em que a pessoa usa o Financeiro, na ordem da lista dela.
   *
   * `null` quer dizer SEM RECORTE: é o super_admin, e o banco decide o que ele
   * vê, como sempre decidiu. Lista vazia quer dizer nenhuma unidade.
   */
  unidadesLiberadas: string[] | null
}

/** True para os papéis cujo acesso depende da chave de cada unidade. */
export function papelPelaChaveDaUnidade(papel: string | null | undefined): boolean {
  return (PAPEIS_PELA_CHAVE_DA_UNIDADE as readonly string[]).includes(papel ?? "")
}

/**
 * A regra inteira.
 *
 * @param papel o papel da pessoa (o maior dela, como o `TenantContext` guarda)
 * @param unidades as unidades da pessoa, na ordem do seletor do topo
 * @param ligadas os ids das unidades com o Financeiro ligado (pode trazer
 *   unidade que não é da pessoa: só conta a interseção)
 */
export function acessoAoFinanceiro(
  papel: string | null | undefined,
  unidades: readonly UnidadeDaPessoa[],
  ligadas: Iterable<string>,
): AcessoAoFinanceiro {
  if (papel === "super_admin") return { pode: true, motivo: null, unidadesLiberadas: null }
  if (!papelPelaChaveDaUnidade(papel)) return { pode: false, motivo: "papel", unidadesLiberadas: [] }

  const comChave = new Set(ligadas)
  const liberadas: string[] = []
  for (const u of unidades) {
    // Unidade desativada não aparece em lugar nenhum do sistema (#222), e o
    // Financeiro não é exceção, mesmo com a chave ligada.
    if (u.deactivated_at) continue
    if (comChave.has(u.id) && !liberadas.includes(u.id)) liberadas.push(u.id)
  }
  return liberadas.length > 0
    ? { pode: true, motivo: null, unidadesLiberadas: liberadas }
    : { pode: false, motivo: "sem_unidade_ligada", unidadesLiberadas: [] }
}

/**
 * O recorte das leituras do módulo, a partir do escopo do seletor do topo.
 *
 * - `unidade`: uma unidade escolhida, em que a pessoa usa o Financeiro.
 * - `unidades`: "Todas as unidades" de quem tem recorte: SÓ as ligadas dela.
 *   Antes, essa leitura ia sem filtro e a política do banco devolvia todas as
 *   unidades da pessoa, inclusive as desligadas.
 * - `permissao`: "Todas as unidades" do super_admin. Sem filtro, como sempre.
 * - `desligada`: pedido com uma unidade da pessoa sem o Financeiro. O módulo
 *   nunca chega aqui, porque pergunta antes o `escopoEfetivoDoFinanceiro`; se um
 *   dia chegar, não lê nada.
 * - `sem_escopo`: nem unidade, nem "Todas as unidades" (o contexto ainda não
 *   resolveu). Não lê.
 */
export type RecorteDoFinanceiro =
  | { tipo: "unidade"; tenantId: string }
  | { tipo: "unidades"; tenantIds: string[] }
  | { tipo: "permissao" }
  | { tipo: "desligada"; tenantId: string }
  | { tipo: "sem_escopo" }

export function recorteDoFinanceiro(
  unidadesLiberadas: readonly string[] | null,
  viewAll: boolean,
  activeTenantId: string | null | undefined,
): RecorteDoFinanceiro {
  if (viewAll) {
    return unidadesLiberadas === null
      ? { tipo: "permissao" }
      : { tipo: "unidades", tenantIds: [...unidadesLiberadas] }
  }
  if (!activeTenantId) return { tipo: "sem_escopo" }
  if (unidadesLiberadas !== null && !unidadesLiberadas.includes(activeTenantId)) {
    return { tipo: "desligada", tenantId: activeTenantId }
  }
  return { tipo: "unidade", tenantId: activeTenantId }
}

/**
 * Um id que não é de unidade nenhuma. Filtro que não pode casar, para o recorte
 * que não lê: mais seguro que mandar a consulta sem filtro e depender do banco.
 */
const NENHUMA_UNIDADE = "00000000-0000-0000-0000-000000000000"

interface ConsultaComFiltro<Q> {
  eq(coluna: string, valor: string): Q
  in(coluna: string, valores: readonly string[]): Q
}

/**
 * Põe o recorte numa consulta do Supabase que tem a coluna `tenant_id`.
 *
 * Substitui o `if (!viewAll && activeTenantId) q = q.eq("tenant_id", ...)` das
 * leituras do módulo, que em "Todas as unidades" mandava a consulta sem filtro.
 */
export function aplicarRecorte<Q extends ConsultaComFiltro<Q>>(q: Q, recorte: RecorteDoFinanceiro): Q {
  switch (recorte.tipo) {
    case "unidade":
      return q.eq("tenant_id", recorte.tenantId)
    case "unidades":
      return recorte.tenantIds.length > 0
        ? q.in("tenant_id", recorte.tenantIds)
        : q.eq("tenant_id", NENHUMA_UNIDADE)
    case "permissao":
      return q
    default:
      return q.eq("tenant_id", NENHUMA_UNIDADE)
  }
}

/** O escopo que o Financeiro usa, a partir do escopo global do seletor do topo. */
export interface EscopoEfetivo {
  viewAll: boolean
  /** A unidade do escopo; em "Todas as unidades", uma das liberadas (nunca uma desligada). */
  activeTenantId: string | undefined
  /** O valor do seletor do topo dentro do Financeiro: "all" ou o id da unidade. */
  valorDoSeletor: string
  /**
   * True quando o escopo global aponta para uma unidade sem o Financeiro e o
   * módulo usa outro. A escolha global não muda: ela continua valendo no resto do
   * sistema.
   */
  ajustado: boolean
}

/**
 * O escopo que o Financeiro usa (decisão do PM, 20/10/2026: unidade que não é do
 * Financeiro da pessoa não aparece para ela dentro do módulo).
 *
 * O seletor do topo é um só para o sistema inteiro. Quem escolheu no Funil uma
 * unidade sem o Financeiro e abre o Financeiro cai direto nas unidades ligadas:
 * com uma ligada, nela; com duas ou mais, em "Todas as unidades" (que no módulo
 * são só as ligadas). A escolha global NÃO é trocada: de volta ao Funil, a
 * pessoa encontra a unidade que tinha escolhido. Só um clique no seletor muda o
 * escopo global.
 *
 * Sem recorte (super_admin), o escopo é o de sempre.
 */
export function escopoEfetivoDoFinanceiro(
  unidadesLiberadas: readonly string[] | null,
  global: { selectedScope: string; viewAll: boolean; activeTenantId: string | undefined },
): EscopoEfetivo {
  const { selectedScope, viewAll, activeTenantId } = global
  if (unidadesLiberadas === null) {
    return { viewAll, activeTenantId, valorDoSeletor: selectedScope, ajustado: false }
  }
  const liberada = (id: string | undefined): id is string => !!id && unidadesLiberadas.includes(id)

  if (viewAll) {
    // Em "Todas as unidades" o contexto aponta `activeTenantId` para uma unidade
    // qualquer da pessoa; no Financeiro, sempre uma liberada.
    return {
      viewAll: true,
      activeTenantId: liberada(activeTenantId) ? activeTenantId : unidadesLiberadas[0],
      valorDoSeletor: "all",
      ajustado: false,
    }
  }
  if (!activeTenantId) return { viewAll: false, activeTenantId: undefined, valorDoSeletor: selectedScope, ajustado: false }
  if (liberada(activeTenantId)) {
    return { viewAll: false, activeTenantId, valorDoSeletor: activeTenantId, ajustado: false }
  }
  // A unidade escolhida não tem o Financeiro.
  if (unidadesLiberadas.length === 0) {
    return { viewAll: false, activeTenantId: undefined, valorDoSeletor: selectedScope, ajustado: false }
  }
  if (unidadesLiberadas.length === 1) {
    const unica = unidadesLiberadas[0]
    return { viewAll: false, activeTenantId: unica, valorDoSeletor: unica, ajustado: true }
  }
  return { viewAll: true, activeTenantId: unidadesLiberadas[0], valorDoSeletor: "all", ajustado: true }
}

/**
 * As unidades do seletor do topo dentro do Financeiro: sem recorte (super_admin),
 * a lista de sempre; com recorte, só as ligadas. Unidade sem o Financeiro não
 * aparece, nem quando é a escolhida no resto do sistema: o valor do seletor no
 * módulo é o do `escopoEfetivoDoFinanceiro`, que é sempre uma das opções (o
 * seletor não fica em branco).
 */
export function unidadesDoSeletorNoFinanceiro<U extends UnidadeDaPessoa>(
  unidades: readonly U[],
  unidadesLiberadas: readonly string[] | null,
): U[] {
  if (unidadesLiberadas === null) return [...unidades]
  return unidades.filter((u) => unidadesLiberadas.includes(u.id))
}
